/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import React, { useEffect, useRef, useState } from 'react';
import { Asset } from '@wit/vtv-x-foundation-lib';
import translate from 'services/i18n/translate';
import { PiRailUi } from 'store/ducks/programInfo/data';
import { Button } from '.';
import { ButtonType } from './Button';
import Grid from './Grid';

const ANIMATION_DURATION = 200;

interface Props {
  data: PiRailUi[];
  activeSeason?: number | string;
  onCardClick?: (asset: Asset, menuItemId?: number) => void;
  menuItemId?: number;
}

const renderSeasonsFilter = (
  data: PiRailUi[],
  selectedSeason: number,
  onFilterClick: (seasonIndex: number) => void
) => {
  return data.map((season, index) => (
    <Button
      key={season.key}
      bemBlock="SeasonTabs"
      bemModifier={season.key}
      buttonType={ButtonType.TAB}
      active={index === selectedSeason}
      text={season.title}
      onClick={() => onFilterClick(index)}
    />
  ));
};

export default function SeasonsWrapper({ data, activeSeason, onCardClick, menuItemId }: Props) {
  const [selectedSeasonIndex, setSelectedSeasonIndex] = useState(0);
  const [isChangingFilter, setIsChangingFilter] = useState(false);

  const timeoutRoutineRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (activeSeason) {
      setSelectedSeasonIndex(data.findIndex((el) => el.key === activeSeason.toString()));
    }
  }, [activeSeason, data]);

  const onFilterClick = (seasonIndex: number) => {
    if (timeoutRoutineRef.current) {
      clearTimeout(timeoutRoutineRef.current);
    }

    setIsChangingFilter(true);

    timeoutRoutineRef.current = setTimeout(() => {
      setSelectedSeasonIndex(seasonIndex);
      setIsChangingFilter(false);
    }, ANIMATION_DURATION);
  };

  return (
    <div id="Seasons" className="seasons-wrapper moreepisodes">
      <div className="component-inner">
        <h2 id="Seasons_title">{translate['programInfo.moreepisodes.title']}</h2>
        {data.length ? (
          <>
            <div id="Seasons_buttonGroup" className="btn-group search-filter">
              <span className="filter-title">{translate['programInfo.moreepisodes.season']}</span>
              {renderSeasonsFilter(data, selectedSeasonIndex, onFilterClick)}
            </div>

            <Grid
              className={`episodes-rail no-background ${isChangingFilter ? 'rail-fadeout' : 'rail-fadein'}`}
              railId="episodes"
              menuItemId={menuItemId}
              assets={data[selectedSeasonIndex]?.assets || []}
              showSeriesInfo
              identifyScheduled
              onCardClick={onCardClick}
            />
          </>
        ) : (
          <span id="Seasons_empty" className="filter-title">
            {translate['programInfo.moreepisodes.noEpisodes']}
          </span>
        )}
      </div>
    </div>
  );
}
